(() => {
  const instances = new Map();

  class WorkshopCustomerRides {
    constructor(root) {
      this.root = root;
      this.track = root.querySelector('[data-wsrides-track]');
      this.cards = this.track ? Array.from(this.track.querySelectorAll('[data-wsrides-card]')) : [];
      this.previousButton = root.querySelector('[data-wsrides-prev]');
      this.nextButton = root.querySelector('[data-wsrides-next]');
      this.autoplayButton = root.querySelector('[data-wsrides-autoplay]');
      this.currentOutput = root.querySelector('[data-wsrides-current]');
      this.liveOutput = root.querySelector('[data-wsrides-live]');
      this.progress = root.querySelector('[data-wsrides-progress]');
      this.dialog = root.querySelector('[data-wsrides-dialog]');
      this.dialogOpenButton = root.querySelector('[data-wsrides-dialog-open]');
      this.dialogCloseButtons = this.dialog
        ? Array.from(this.dialog.querySelectorAll('[data-wsrides-dialog-close]'))
        : [];
      this.submissionForm = this.dialog ? this.dialog.closest('form') : null;
      this.submitButton = this.dialog ? this.dialog.querySelector('[data-wsrides-submit]') : null;
      this.motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.autoplayEnabled = root.dataset.autoplay === 'true';
      this.autoplayDelay = Math.max(3000, Number(root.dataset.autoplayDelay) || 6000);
      this.activeIndex = -1;
      this.userPaused = false;
      this.pauseReasons = new Set();
      this.listeners = [];
      this.autoplayTimer = null;
      this.interactionTimer = null;
      this.scrollFrame = null;
      this.suppressClick = false;
      this.drag = null;
      this.dialogCloseTimer = null;
      this.lastFocusedElement = null;

      if ((this.track && this.cards.length) || this.dialog) this.init();
    }

    listen(target, type, handler, options) {
      if (!target) return;
      target.addEventListener(type, handler, options);
      this.listeners.push([target, type, handler, options]);
    }

    init() {
      this.root.style.setProperty('--rides-count', String(this.cards.length));

      this.listen(this.dialogOpenButton, 'click', () => this.openDialog());
      this.dialogCloseButtons.forEach((button) => {
        this.listen(button, 'click', () => this.closeDialog());
      });
      this.listen(this.dialog, 'cancel', (event) => {
        event.preventDefault();
        this.closeDialog();
      });
      this.listen(this.dialog, 'click', (event) => this.onDialogBackdropClick(event));
      this.listen(this.dialog, 'keydown', (event) => this.onDialogKeydown(event));
      this.listen(this.submissionForm, 'submit', () => this.onFormSubmit());

      this.listen(this.previousButton, 'click', () => {
        this.registerInteraction();
        this.goTo(this.activeIndex - 1);
      });

      this.listen(this.nextButton, 'click', () => {
        this.registerInteraction();
        this.goTo(this.activeIndex + 1);
      });

      this.listen(this.autoplayButton, 'click', () => this.toggleAutoplay());
      this.listen(this.track, 'scroll', () => this.requestDepthUpdate(), { passive: true });
      this.listen(this.track, 'keydown', (event) => this.onKeydown(event));
      this.listen(this.track, 'pointerdown', (event) => this.onPointerDown(event));
      this.listen(this.track, 'pointermove', (event) => this.onPointerMove(event));
      this.listen(this.track, 'pointerup', (event) => this.onPointerUp(event));
      this.listen(this.track, 'pointercancel', (event) => this.onPointerUp(event));
      this.listen(this.track, 'click', (event) => this.onClick(event), true);
      this.listen(this.track, 'pointerenter', (event) => {
        if (event.pointerType !== 'touch') this.pause('hover');
      });
      this.listen(this.track, 'pointerleave', (event) => {
        if (event.pointerType !== 'touch') this.resume('hover');
      });
      this.listen(this.track, 'focusin', () => this.pause('focus'));
      this.listen(this.track, 'focusout', (event) => {
        if (!this.track.contains(event.relatedTarget)) this.resume('focus');
      });
      this.listen(this.track, 'wheel', () => this.registerInteraction(), { passive: true });
      this.listen(document, 'visibilitychange', () => this.onVisibilityChange());
      this.listen(window, 'resize', () => this.requestDepthUpdate(), { passive: true });

      this.cards.forEach((card) => {
        this.listen(card, 'pointermove', (event) => this.onCardPointerMove(card, event));
        this.listen(card, 'pointerleave', () => this.resetCardTilt(card));
      });

      if (this.motionQuery.addEventListener) {
        this.motionChangeHandler = () => this.onMotionChange();
        this.motionQuery.addEventListener('change', this.motionChangeHandler);
      }

      if (this.track && 'ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(() => this.requestDepthUpdate());
        this.resizeObserver.observe(this.track);
      }

      requestAnimationFrame(() => {
        this.updateDepth();
        this.updateAutoplayButton();
        this.scheduleAutoplay();
        if (this.dialog && this.dialog.hasAttribute('data-wsrides-auto-open')) this.openDialog(true);
      });
    }

    openDialog(fromServerResponse) {
      if (!this.dialog || this.dialog.open) return;

      window.clearTimeout(this.dialogCloseTimer);
      this.lastFocusedElement = fromServerResponse ? this.dialogOpenButton : document.activeElement;

      if (typeof this.dialog.showModal === 'function') {
        try {
          this.dialog.showModal();
        } catch (error) {
          this.dialog.setAttribute('open', '');
        }
      } else {
        this.dialog.setAttribute('open', '');
      }

      this.root.classList.add('is-dialog-open');
      document.body.classList.add('wsrides-dialog-open');
      this.pause('dialog');

      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          if (!this.dialog || !this.dialog.open) return;
          this.dialog.classList.add('is-open');
          this.focusDialogStart();
        });
      });
    }

    closeDialog(options) {
      if (!this.dialog || !this.dialog.open) return;

      const settings = options || {};
      window.clearTimeout(this.dialogCloseTimer);
      const finish = () => {
        window.clearTimeout(this.dialogCloseTimer);
        this.dialogCloseTimer = null;

        if (this.dialog.open && typeof this.dialog.close === 'function') this.dialog.close();
        else this.dialog.removeAttribute('open');

        this.dialog.classList.remove('is-open');
        this.root.classList.remove('is-dialog-open');
        if (!document.querySelector('[data-wsrides-dialog][open]')) {
          document.body.classList.remove('wsrides-dialog-open');
        }
        this.resume('dialog');

        if (settings.restoreFocus !== false && this.lastFocusedElement && this.lastFocusedElement.isConnected) {
          this.lastFocusedElement.focus({ preventScroll: true });
        }
        this.lastFocusedElement = null;
      };

      this.dialog.classList.remove('is-open');
      if (settings.immediate || this.motionQuery.matches) finish();
      else this.dialogCloseTimer = window.setTimeout(finish, 300);
    }

    focusDialogStart() {
      if (!this.dialog || !this.dialog.open) return;
      const target =
        this.dialog.querySelector('[data-wsrides-status]') ||
        this.dialog.querySelector('[data-wsrides-initial-focus]') ||
        this.dialog.querySelector('[data-wsrides-dialog-close]');
      if (target) target.focus({ preventScroll: true });
    }

    onDialogBackdropClick(event) {
      if (event.target !== this.dialog) return;
      const rect = this.dialog.getBoundingClientRect();
      const isOutside =
        event.clientX < rect.left ||
        event.clientX > rect.right ||
        event.clientY < rect.top ||
        event.clientY > rect.bottom;
      if (isOutside) this.closeDialog();
    }

    onDialogKeydown(event) {
      if (event.key === 'Escape') {
        event.preventDefault();
        this.closeDialog();
        return;
      }

      if (event.key !== 'Tab') return;
      const focusable = this.dialogFocusableElements();
      if (!focusable.length) {
        event.preventDefault();
        return;
      }

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const activeIsFocusable = focusable.includes(document.activeElement);
      if (event.shiftKey && (document.activeElement === first || !activeIsFocusable)) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && (document.activeElement === last || !activeIsFocusable)) {
        event.preventDefault();
        first.focus();
      }
    }

    dialogFocusableElements() {
      if (!this.dialog) return [];
      const selector = [
        'a[href]',
        'button:not([disabled])',
        'input:not([disabled]):not([type="hidden"])',
        'select:not([disabled])',
        'textarea:not([disabled])',
        '[tabindex]:not([tabindex="-1"])'
      ].join(',');

      return Array.from(this.dialog.querySelectorAll(selector)).filter((element) => {
        return !element.hidden && element.getClientRects().length > 0;
      });
    }

    onFormSubmit() {
      if (!this.submitButton) return;
      this.submitButton.disabled = true;
      this.submitButton.setAttribute('aria-busy', 'true');
      const label = this.submitButton.querySelector('[data-wsrides-submit-label]');
      if (label) label.textContent = 'SENDING…';
    }

    onKeydown(event) {
      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
      event.preventDefault();
      this.registerInteraction();
      this.goTo(this.activeIndex + (event.key === 'ArrowRight' ? 1 : -1));
    }

    onPointerDown(event) {
      this.pause('interaction');

      if (event.pointerType !== 'mouse' || event.button !== 0) return;

      this.drag = {
        id: event.pointerId,
        startX: event.clientX,
        startScroll: this.track.scrollLeft,
        moved: false
      };
      this.track.classList.add('is-dragging');
      this.track.setPointerCapture(event.pointerId);
    }

    onPointerMove(event) {
      if (!this.drag || this.drag.id !== event.pointerId) return;
      const distance = event.clientX - this.drag.startX;
      if (Math.abs(distance) > 4) this.drag.moved = true;
      if (!this.drag.moved) return;
      event.preventDefault();
      this.track.scrollLeft = this.drag.startScroll - distance;
    }

    onPointerUp(event) {
      if (this.drag && this.drag.id === event.pointerId) {
        const moved = this.drag.moved;
        if (this.track.hasPointerCapture(event.pointerId)) this.track.releasePointerCapture(event.pointerId);
        this.track.classList.remove('is-dragging');
        this.drag = null;

        if (moved) {
          this.suppressClick = true;
          window.setTimeout(() => {
            this.suppressClick = false;
          }, 120);
          this.goTo(this.closestCardIndex(), true);
        }
      }

      this.delayResume('interaction');
    }

    onClick(event) {
      if (!this.suppressClick) return;
      event.preventDefault();
      event.stopPropagation();
      this.suppressClick = false;
    }

    onCardPointerMove(card, event) {
      if (this.motionQuery.matches || event.pointerType === 'touch' || this.drag) return;
      const rect = card.getBoundingClientRect();
      const x = (event.clientX - rect.left) / rect.width - .5;
      const y = (event.clientY - rect.top) / rect.height - .5;
      card.style.setProperty('--ride-tilt-x', String((-y * 4).toFixed(2)) + 'deg');
      card.style.setProperty('--ride-tilt-y', String((x * 5).toFixed(2)) + 'deg');
      card.style.setProperty('--shine-x', String(((x + .5) * 100).toFixed(1)) + '%');
      card.style.setProperty('--shine-y', String(((y + .5) * 100).toFixed(1)) + '%');
    }

    resetCardTilt(card) {
      card.style.setProperty('--ride-tilt-x', '0deg');
      card.style.setProperty('--ride-tilt-y', '0deg');
      card.style.setProperty('--shine-x', '50%');
      card.style.setProperty('--shine-y', '45%');
    }

    requestDepthUpdate() {
      if (this.scrollFrame) return;
      this.scrollFrame = requestAnimationFrame(() => {
        this.scrollFrame = null;
        this.updateDepth();
      });
    }

    updateDepth() {
      if (!this.track || !this.cards.length) return;
      const trackRect = this.track.getBoundingClientRect();
      const trackCenter = trackRect.left + trackRect.width / 2;
      let closestIndex = 0;
      let closestDistance = Infinity;

      this.cards.forEach((card, index) => {
        const rect = card.getBoundingClientRect();
        const distance = rect.left + rect.width / 2 - trackCenter;
        const normalized = Math.max(-2.2, Math.min(2.2, distance / Math.max(rect.width * .88, 1)));
        const absolute = Math.abs(normalized);
        const focus = Math.max(0, 1 - Math.min(absolute, 1));

        card.style.setProperty('--ride-turn', String((-normalized * 9).toFixed(2)) + 'deg');
        card.style.setProperty('--ride-lift', String(((1 - focus) * 24).toFixed(1)) + 'px');
        card.style.setProperty('--ride-depth', String((focus * 72).toFixed(1)) + 'px');
        card.style.setProperty('--ride-scale', String((.86 + focus * .14).toFixed(3)));
        card.style.setProperty('--ride-brightness', String((.57 + focus * .43).toFixed(3)));
        card.style.setProperty('--ride-opacity', String((.64 + focus * .36).toFixed(3)));

        if (Math.abs(distance) < closestDistance) {
          closestDistance = Math.abs(distance);
          closestIndex = index;
        }
      });

      this.setActive(closestIndex);
    }

    closestCardIndex() {
      const trackRect = this.track.getBoundingClientRect();
      const center = trackRect.left + trackRect.width / 2;
      let index = 0;
      let distance = Infinity;

      this.cards.forEach((card, cardIndex) => {
        const rect = card.getBoundingClientRect();
        const cardDistance = Math.abs(rect.left + rect.width / 2 - center);
        if (cardDistance < distance) {
          distance = cardDistance;
          index = cardIndex;
        }
      });

      return index;
    }

    setActive(index) {
      if (index === this.activeIndex) return;
      this.activeIndex = index;

      this.cards.forEach((card, cardIndex) => {
        const isActive = cardIndex === index;
        card.classList.toggle('is-active', isActive);
        if (isActive) card.setAttribute('aria-current', 'true');
        else card.removeAttribute('aria-current');
      });

      const visibleIndex = String(index + 1).padStart(2, '0');
      if (this.currentOutput) this.currentOutput.textContent = visibleIndex;
      if (this.liveOutput) this.liveOutput.textContent = 'Showing ride ' + String(index + 1) + ' of ' + String(this.cards.length);
      if (this.progress) this.progress.style.transform = 'scaleX(' + String((index + 1) / this.cards.length) + ')';
    }

    goTo(index, fromDrag) {
      if (!this.cards.length) return;
      const targetIndex = (index % this.cards.length + this.cards.length) % this.cards.length;
      const card = this.cards[targetIndex];
      const trackRect = this.track.getBoundingClientRect();
      const cardRect = card.getBoundingClientRect();
      const centerDelta = cardRect.left + cardRect.width / 2 - (trackRect.left + trackRect.width / 2);
      const left = this.track.scrollLeft + centerDelta;

      this.setActive(targetIndex);
      this.track.scrollTo({
        left: left,
        behavior: this.motionQuery.matches || fromDrag ? 'auto' : 'smooth'
      });
    }

    registerInteraction() {
      this.pause('interaction');
      this.delayResume('interaction');
    }

    delayResume(reason) {
      window.clearTimeout(this.interactionTimer);
      this.interactionTimer = window.setTimeout(() => this.resume(reason), 2600);
    }

    pause(reason) {
      this.pauseReasons.add(reason);
      this.clearAutoplay();
    }

    resume(reason) {
      this.pauseReasons.delete(reason);
      this.scheduleAutoplay();
    }

    clearAutoplay() {
      window.clearTimeout(this.autoplayTimer);
      this.autoplayTimer = null;
    }

    scheduleAutoplay() {
      this.clearAutoplay();
      if (
        !this.autoplayEnabled ||
        this.userPaused ||
        this.motionQuery.matches ||
        this.pauseReasons.size ||
        this.cards.length < 2
      ) return;

      this.autoplayTimer = window.setTimeout(() => {
        this.goTo(this.activeIndex + 1);
        this.scheduleAutoplay();
      }, this.autoplayDelay);
    }

    toggleAutoplay() {
      this.userPaused = !this.userPaused;
      this.updateAutoplayButton();
      if (this.userPaused) this.clearAutoplay();
      else this.scheduleAutoplay();
    }

    updateAutoplayButton() {
      if (!this.autoplayButton) return;
      if (this.motionQuery.matches || this.cards.length < 2) {
        this.autoplayButton.hidden = true;
        return;
      }

      this.autoplayButton.hidden = false;
      this.autoplayButton.dataset.state = this.userPaused ? 'paused' : 'running';
      this.autoplayButton.setAttribute('aria-pressed', this.userPaused ? 'true' : 'false');
      this.autoplayButton.setAttribute(
        'aria-label',
        this.userPaused ? 'Resume customer rides autoplay' : 'Pause customer rides autoplay'
      );
      this.autoplayButton.title = this.userPaused ? 'Resume autoplay' : 'Pause autoplay';
    }

    onVisibilityChange() {
      if (document.hidden) this.pause('hidden');
      else this.resume('hidden');
    }

    onMotionChange() {
      this.cards.forEach((card) => this.resetCardTilt(card));
      this.updateAutoplayButton();
      this.scheduleAutoplay();
    }

    selectBlock(block) {
      const card = block && block.closest ? block.closest('[data-wsrides-card]') : null;
      if (!card || !this.root.contains(card)) return;
      const index = this.cards.indexOf(card);
      if (index > -1) {
        this.pause('editor');
        this.goTo(index);
      }
    }

    deselectBlock(block) {
      if (block && this.root.contains(block)) this.resume('editor');
    }

    destroy() {
      if (this.dialog && this.dialog.open) this.closeDialog({ immediate: true, restoreFocus: false });
      this.clearAutoplay();
      window.clearTimeout(this.interactionTimer);
      window.clearTimeout(this.dialogCloseTimer);
      if (this.scrollFrame) cancelAnimationFrame(this.scrollFrame);
      if (this.resizeObserver) this.resizeObserver.disconnect();
      if (this.motionChangeHandler && this.motionQuery.removeEventListener) {
        this.motionQuery.removeEventListener('change', this.motionChangeHandler);
      }
      this.listeners.forEach((listener) => listener[0].removeEventListener(listener[1], listener[2], listener[3]));
      this.listeners = [];
    }
  }

  function rootsIn(scope) {
    const roots = [];
    if (scope.matches && scope.matches('[data-wsrides]')) roots.push(scope);
    if (scope.querySelectorAll) roots.push(...scope.querySelectorAll('[data-wsrides]'));
    return roots;
  }

  function mount(scope) {
    rootsIn(scope || document).forEach((root) => {
      if (!instances.has(root)) instances.set(root, new WorkshopCustomerRides(root));
    });
  }

  mount(document);

  document.addEventListener('shopify:section:load', (event) => mount(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    rootsIn(event.target).forEach((root) => {
      const instance = instances.get(root);
      if (instance) instance.destroy();
      instances.delete(root);
    });
  });
  document.addEventListener('shopify:block:select', (event) => {
    instances.forEach((instance) => instance.selectBlock(event.target));
  });
  document.addEventListener('shopify:block:deselect', (event) => {
    instances.forEach((instance) => instance.deselectBlock(event.target));
  });
})();
